/**
 * Canvas page-header pattern (TMMRSL-0005): kicker / title / subtitle, optional
 * actions on the end side. Screens from 0006 on render this at the top.
 * On xs/sm the actions slot wraps to its own row below the title block so
 * PeriodPicker custom dates are not squeezed beside the heading.
 * The kicker is always `pages.<id>.kicker`; an object page (TMMRSL-0041) passes its record's own
 * `title` and `subtitle`, which replace `pages.<id>.title` / `.subtitle` (an empty `subtitle`
 * renders none).
 *
 * Every page is an `h1` with its kicker (TMMRSL-0058). `document.title` is `page · console`.
 *
 * A detail page passes `breadcrumbs` (TMM-B7, TMMRSL-0072), rendered above the kicker: every item
 * but the last is a link (its `to` may carry the list's query), the last is the current page.
 */
import { useEffect, type ReactNode } from 'react';
import { Box, Breadcrumbs, Link, Typography, useMediaQuery } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, type To } from 'react-router-dom';

export interface Breadcrumb {
  label: string;
  /** Where the item links; the last item (the current page) has none. */
  to?: To;
}

export interface PageHeaderProps {
  /** Screen id from the nav / canvas (`dashboard`, `agreement`, …). */
  id: string;
  /** Replaces `pages.<id>.title` (an object page's record name). */
  title?: string;
  /** Replaces `pages.<id>.subtitle`; `''` renders no subtitle. */
  subtitle?: string;
  actions?: ReactNode;
  /** The trail to this page, parent first, the page itself last. */
  breadcrumbs?: Breadcrumb[];
}

/**
 * The breadcrumb trail. A detail page renders it on its own while its record loads or fails (there
 * is no header yet), so the way back to the list is always there.
 */
export function PageBreadcrumbs({ items }: { items: Breadcrumb[] }) {
  const { t } = useTranslation();
  const last = items.length - 1;
  return (
    <Breadcrumbs aria-label={t('common.breadcrumb')} sx={{ mb: 1 }}>
      {items.map((crumb, index) =>
        index < last && crumb.to !== undefined ? (
          <Link key={index} component={RouterLink} to={crumb.to} variant="body2">
            {crumb.label}
          </Link>
        ) : (
          <Typography
            key={index}
            variant="body2"
            aria-current={index === last ? 'page' : undefined}
            sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}
          >
            {crumb.label}
          </Typography>
        ),
      )}
    </Breadcrumbs>
  );
}

export function PageHeader({ id, title, subtitle, actions, breadcrumbs }: PageHeaderProps) {
  const { t, i18n } = useTranslation();
  const compact = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const subtitleText = subtitle ?? t(`pages.${id}.subtitle`);
  const pageTitleKey = `pages.${id}.title`;
  const documentPage = i18n.exists(pageTitleKey) ? t(pageTitleKey) : (title ?? '');
  const consoleName = t('app.consoleName');

  useEffect(() => {
    document.title = [documentPage, consoleName].filter(Boolean).join(' · ');
  }, [documentPage, consoleName]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: compact ? 'column' : 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        mb: 2.5,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {breadcrumbs && breadcrumbs.length > 0 ? <PageBreadcrumbs items={breadcrumbs} /> : null}
        <Typography variant="overline" component="p">
          {t(`pages.${id}.kicker`)}
        </Typography>
        <Typography
          variant="h1"
          component="h1"
          tabIndex={-1}
          sx={{ '&:focus:not(:focus-visible)': { outline: 'none' } }}
        >
          {title ?? t(pageTitleKey)}
        </Typography>
        {subtitleText ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {subtitleText}
          </Typography>
        ) : null}
      </Box>
      {actions ? <Box sx={{ flexShrink: 0, width: compact ? 1 : 'auto' }}>{actions}</Box> : null}
    </Box>
  );
}
